import React, {useState, useEffect} from 'react';
import {Stack, Grid} from '@mui/material';
import {t} from 'i18next';
import '../FieldTypes.scss';
import {ISelectItem} from '../../../../../../shared/types';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import ArrAutocomplete from '../../../../../common/arr-autocomplete/ArrAutocomplete';
import {FieldTypeProps} from '../FieldTypeProps';
import {
  useFieldData,
  useAutocomplete,
} from '../../../../../../hooks/session-search/useSessionSearch';
import {formatAutocompleteResults} from '../../autocompleteUtils';

const CommentsFieldType: React.FC<FieldTypeProps> = ({
  type,
  onCriteriaChaged,
  onValidated,
}) => {
  const [fieldSelectItems, setFieldSelectItems] = useState<ISelectItem[]>([]);
  const [fieldSelectedItem, setFieldSelectedItem] =
    useState<ISelectItem | null>(null);
  const [textAreaValue, setTextAreaValue] = useState<string>('');

  const [autocompleteValue, setAutocompleteValue] = useState<string>('');
  const [autocompleteEnabled, setAutocompleteEnabled] =
    useState<boolean>(false);
  const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
  const {data: autocompleteData, refetch} = useAutocomplete(
    autocompleteValue,
    true
  );

  const {data: fieldData} = useFieldData(type);

  useEffect(() => {
    if (fieldData) {
      const items = fieldData.workProductItemsDetails.map((w) => ({
        value: w.id,
        label: w.text,
      })) as ISelectItem[];

      setFieldSelectItems(items);

      if (!fieldSelectedItem && items.length > 0) {
        setFieldSelectedItem(items[0]);
      }
    }
  }, [fieldData]);

  useEffect(() => {
    if (textAreaValue && textAreaValue.length >= 3) {
      setAutocompleteValue(`${fieldSelectedItem?.label}:${textAreaValue}`);
    }
    onValidated?.(!!textAreaValue);
  }, [textAreaValue]);

  useEffect(() => {
    refetch();
  }, [autocompleteValue]);

  useEffect(() => {
    if (fieldSelectedItem) {
      const selectedField = fieldData?.workProductItemsDetails.find(
        (w) => w.id === fieldSelectedItem.value
      );
      if (selectedField) {
        onCriteriaChaged?.(selectedField, 'is', [textAreaValue]);
      }
    }
  }, [fieldSelectedItem, textAreaValue]);

  useEffect(() => {
    const suggestions = formatAutocompleteResults(autocompleteData ?? []);
    setAutocompleteItems(suggestions);
  }, [autocompleteData]);

  return (
    <Stack spacing={2} className="field-type-content">
      <Grid container spacing={2} className="field-type-selection">
        <Grid size="grow" className="field-selection">
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'sessionSearch.queryList.contentAndMetadata.commentsDrawer.selectField'
            )}
            dropdownItemKey="value"
            dropdownItemDisplay="label"
            dropdownItems={fieldSelectItems}
            selectedValues={fieldSelectedItem}
            onSelectionChange={setFieldSelectedItem}
            sizeVariant="large"
            dataTestId="asd-select-comments-field"
            searchable
            showNoResultsText
          />
        </Grid>
        <Grid className="field-separator"></Grid>
        <Grid size="grow" className="field-operator"></Grid>
      </Grid>
      <Grid className="field-type-value">
        {fieldSelectedItem && (
          <ArrAutocomplete
            value={textAreaValue}
            options={autocompleteItems}
            onChange={setTextAreaValue}
            enabled={autocompleteEnabled}
            data-testid="comments-field-type-autocomplete"
          >
            <ArrTextArea
              value={textAreaValue}
              placeholder={t(
                'sessionSearch.queryList.contentAndMetadata.commentsDrawer.enterValue'
              )}
              rows={6}
              resizable={true}
              onChange={setTextAreaValue}
              onFocus={() => setAutocompleteEnabled(true)}
              onBlur={() => setAutocompleteEnabled(false)}
              dataTestId={`text-area-comment-field`}
            />
          </ArrAutocomplete>
        )}
      </Grid>
    </Stack>
  );
};

export default CommentsFieldType;
